import React, { useState, useRef, useEffect } from 'react';
import { useInternalChat } from '../../context/InternalChatContext';
import TeamChatThread from './TeamChatThread';

function formatRelative(dateStr) {
    if (!dateStr) return '';
    const now = new Date();
    const d = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return `${Math.floor(diffHrs / 24)}d ago`;
}

function shortName(id) {
    if (!id) return '?';
    return id.includes('@') ? id.split('@')[0] : id;
}

function TeamMemberSelect({ allMembers, onlineUsers, currentUser, value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const onlineSet = new Set(onlineUsers);
    const others = allMembers.filter(m => m.username !== currentUser?.username);
    const online = others.filter(m => onlineSet.has(m.username));
    const offline = others.filter(m => !onlineSet.has(m.username));

    const selectedMember = value ? others.find(m => m.username === value) : null;
    const selectedIsOnline = selectedMember ? onlineSet.has(selectedMember.username) : false;

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const select = (username) => {
        onChange(username || null);
        setOpen(false);
    };

    return (
        <div className="tc-member-select" ref={ref}>
            <button
                type="button"
                className={`tc-member-select-trigger ${open ? 'open' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <span className="tc-member-select-value">
                    {value ? (
                        <>
                            <span className={`tc-presence-dot ${selectedIsOnline ? 'online' : 'offline'}`} />
                            @{shortName(value)}
                        </>
                    ) : (
                        <span className="tc-member-select-placeholder">Everyone (optional)</span>
                    )}
                </span>
                <svg className="tc-member-select-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <div className="tc-member-dropdown">
                    <div className="tc-member-option tc-member-option-everyone" onClick={() => select(null)}>
                        <span className="tc-member-option-label">Everyone</span>
                        <span className="tc-member-option-sub">Anyone can answer</span>
                    </div>

                    {online.length > 0 && (
                        <>
                            <div className="tc-member-group-label">Online now</div>
                            {online.map(m => (
                                <div
                                    key={m.username}
                                    className={`tc-member-option ${value === m.username ? 'selected' : ''}`}
                                    onClick={() => select(m.username)}
                                >
                                    <span className="tc-presence-dot online" />
                                    <span className="tc-member-option-label">@{shortName(m.username)}</span>
                                </div>
                            ))}
                        </>
                    )}

                    {offline.length > 0 && (
                        <>
                            <div className="tc-member-group-label">Offline — will be emailed</div>
                            {offline.map(m => (
                                <div
                                    key={m.username}
                                    className={`tc-member-option offline ${value === m.username ? 'selected' : ''}`}
                                    onClick={() => select(m.username)}
                                >
                                    <span className="tc-presence-dot offline" />
                                    <span className="tc-member-option-label">@{shortName(m.username)}</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function SessionItem({ session, onClick }) {
    const directed = session.directedTo ? shortName(session.directedTo) : null;
    return (
        <div className="tc-session-item" onClick={onClick}>
            <div className="tc-session-item-title">{session.title}</div>
            <div className="tc-session-item-meta">
                <span className={`tc-status-badge ${session.status.toLowerCase()}`}>
                    {session.status === 'OPEN' ? 'open' : 'resolved'}
                </span>
                {directed && (
                    <span className="tc-directed-tag">→ @{directed}</span>
                )}
                <span>{formatRelative(session.createdAt)}</span>
                <span>by {shortName(session.createdBy)}</span>
            </div>
        </div>
    );
}

export default function TeamChatPanel({ onClose }) {
    const {
        sessions, sessionsHasMore, loadMoreSessions,
        mySessions, mySessionsHasMore, loadMoreMySessions,
        activeSessionId, onlineUsers, allTeamMembers,
        openSession, createSession, user,
    } = useInternalChat();
    const [tab, setTab] = useState('team');
    const [showForm, setShowForm] = useState(false);
    const [question, setQuestion] = useState('');
    const [directedTo, setDirectedTo] = useState(null);
    const [pendingAutoOpen, setPendingAutoOpen] = useState(false);

    // Auto-open the session the user just created as soon as it arrives via WebSocket
    useEffect(() => {
        if (pendingAutoOpen && mySessions.length > 0) {
            openSession(mySessions[0].id);
            setPendingAutoOpen(false);
        }
    }, [mySessions, pendingAutoOpen, openSession]);

    const allSessions = [...sessions, ...mySessions];
    const activeSession = allSessions.find(s => s.id === activeSessionId);

    const onlineCount = onlineUsers.filter(u => u !== user?.username).length;

    const handleSubmit = () => {
        const trimmed = question.trim();
        if (!trimmed) return;
        createSession(trimmed, directedTo);
        setQuestion('');
        setDirectedTo(null);
        setShowForm(false);
        setTab('mine');
        setPendingAutoOpen(true);
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setQuestion('');
        setDirectedTo(null);
    };

    if (activeSession) {
        return <TeamChatThread session={activeSession} />;
    }

    const teamSessions = sessions.filter(s => s.status === 'OPEN');

    return (
        <>
            <div className="tc-panel-header">
                <h3>Team Chat</h3>
                <div className="tc-panel-header-right">
                    {onlineCount > 0 && (
                        <>
                            <div className="tc-online-dot" />
                            <span className="tc-online-count">{onlineCount} online</span>
                        </>
                    )}
                    <button className="tc-close-btn" onClick={onClose} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="tc-tabs">
                <button className={`tc-tab ${tab === 'team' ? 'active' : ''}`} onClick={() => setTab('team')}>
                    Team Questions {teamSessions.length > 0 && `(${teamSessions.length})`}
                </button>
                <button className={`tc-tab ${tab === 'mine' ? 'active' : ''}`} onClick={() => setTab('mine')}>
                    My Questions
                </button>
            </div>

            <div className="tc-session-list">
                {tab === 'team' && (
                    teamSessions.length === 0
                        ? <p className="tc-session-list-empty">No open questions from the team.</p>
                        : <>
                            {teamSessions.map(s => (
                                <SessionItem key={s.id} session={s} onClick={() => openSession(s.id)} />
                            ))}
                            {sessionsHasMore && (
                                <button className="tc-load-more-btn" onClick={loadMoreSessions}>
                                    Load more
                                </button>
                            )}
                        </>
                )}
                {tab === 'mine' && (
                    mySessions.length === 0
                        ? <p className="tc-session-list-empty">You haven't asked any questions yet.</p>
                        : <>
                            {mySessions.map(s => (
                                <SessionItem key={s.id} session={s} onClick={() => openSession(s.id)} />
                            ))}
                            {mySessionsHasMore && (
                                <button className="tc-load-more-btn" onClick={loadMoreMySessions}>
                                    Load more
                                </button>
                            )}
                        </>
                )}
            </div>

            {!showForm && (
                <button className="tc-new-question-btn" onClick={() => setShowForm(true)}>
                    + Ask the team
                </button>
            )}

            {showForm && (
                <div className="tc-new-question-form">
                    <div className="tc-form-field-label">Direct to</div>
                    <TeamMemberSelect
                        allMembers={allTeamMembers.length > 0 ? allTeamMembers : onlineUsers.map(u => ({ username: u, role: '' }))}
                        onlineUsers={onlineUsers}
                        currentUser={user}
                        value={directedTo}
                        onChange={setDirectedTo}
                    />
                    <textarea
                        rows={3}
                        placeholder={directedTo
                            ? `What do you want to ask @${shortName(directedTo)}?`
                            : "What's your question? (be specific)"}
                        value={question}
                        onChange={e => setQuestion(e.target.value)}
                        autoFocus
                    />
                    <div className="tc-form-actions">
                        <button className="tc-btn-cancel" onClick={handleCancelForm}>Cancel</button>
                        <button className="tc-btn-submit" onClick={handleSubmit} disabled={!question.trim()}>Ask</button>
                    </div>
                </div>
            )}
        </>
    );
}
